import type { PurchaseCustomerInfo } from "./purchases";

/**
 * Why an account can't be deleted right now, or null when it can.
 *
 * Deleting an account doesn't cancel its subscription — the store or Stripe
 * keeps billing the customer, and the renewal webhooks then have no account to
 * apply to. So while a subscription is still set to renew, the customer has to
 * cancel it first (Account → Manage subscription). A cancelled subscription
 * that simply hasn't expired yet won't bill again, so it doesn't block. This is
 * a guard for the customer's benefit, enforced in the app; the deletion
 * function itself only ever acts on the caller's own account.
 */
export type AccountDeletionBlocker = "renewing-subscription";

export function accountDeletionBlocker(
  customerInfo: Pick<PurchaseCustomerInfo, "willRenew"> | null | undefined,
): AccountDeletionBlocker | null {
  return customerInfo?.willRenew ? "renewing-subscription" : null;
}

/**
 * The two things a person types to confirm deleting their account. The same
 * rule is enforced again by the request_account_deletion() database function,
 * so this only decides when the confirm button is enabled.
 */
export const DELETE_CONFIRMATION = "DELETE";

export function isDeletionConfirmed({
  phrase,
  email,
  accountEmail,
}: {
  phrase: string;
  email: string;
  /** The account's email; empty for accounts that have none. */
  accountEmail: string;
}) {
  if (phrase.trim() !== DELETE_CONFIRMATION) return false;
  const expected = accountEmail.trim().toLowerCase();
  return !expected || email.trim().toLowerCase() === expected;
}
